import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, Database, ExternalLink, Star, Plus, Trash2, RefreshCw } from 'lucide-react';
import { instanzAdressen } from '../api';
import DnsAnleitung from './DnsAnleitung';
import type { InstanzAdresse, AdressArt, AdressBefund } from '../api';

/**
 * Unter welchen Adressen diese Instanz erreichbar ist.
 *
 * ══ pgADMIN STEHT HIER ALS ADRESSE, NICHT ALS KNOPF ════════════════════════
 *
 * Es hatte einen eigenen Block mit der Begründung, man suche es genau dann,
 * wenn etwas klemmt. Das stimmt — nur sucht man es dann bei der **Datenbank**
 * und nicht in der Adressliste. Der Knopf sitzt deshalb im SQL-Reiter; hier
 * bleibt die Adresse als eine unter den anderen, denn das ist sie.
 */

/**
 * Die Ampel zu einer Adresse.
 *
 * Grau heisst „noch nicht geprueft" und nicht „in Ordnung" — ein Punkt, der
 * schweigt, waehrend er laedt, wuerde als Entwarnung gelesen.
 */
function Ampel({ befund, laedt }: { befund?: AdressBefund; laedt: boolean }) {
  if (laedt && !befund) {
    return <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-slate-300" title="wird geprüft…" />;
  }
  if (!befund) {
    return <span className="h-2 w-2 shrink-0 rounded-full bg-slate-200" title="nicht geprüft" />;
  }
  const farbe =
    befund.ampel === 'gruen' ? 'bg-emerald-500'
    : befund.ampel === 'gelb' ? 'bg-amber-500'
    : 'bg-red-500';
  return (
    <span
      className={`h-2 w-2 shrink-0 rounded-full ${farbe}`}
      title={`${befund.befund} (${befund.dauerMs} ms)`}
    />
  );
}

function Zeile({
  a,
  haupt,
  befund,
  laedt,
  onEntfernen,
  onArt,
  artLaeuft,
}: {
  a: { hostname: string; notiz?: string | null; art?: AdressArt };
  haupt?: boolean;
  befund?: AdressBefund;
  laedt?: boolean;
  onEntfernen?: () => void;
  onArt?: (art: AdressArt) => void;
  artLaeuft?: boolean;
}) {
  return (
    <div className="group py-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <Ampel befund={befund} laedt={Boolean(laedt)} />
          {haupt && <Star size={11} className="shrink-0 text-amber-500" aria-label="führend" />}
          <a
            href={`https://${a.hostname}`}
            target="_blank"
            rel="noreferrer"
            className="truncate font-mono text-xs text-brand-700 hover:underline"
          >
            {a.hostname}
          </a>
          {a.notiz && <span className="shrink-0 text-xs text-slate-400">{a.notiz}</span>}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {/* Die fuehrende Adresse hat keine Auswahl: sie IST das Ziel jeder
              Weiterleitung. Sie auf „leitet weiter" zu stellen hiesse, sie auf
              sich selbst zu leiten. */}
          {onArt && (
            <select
              value={a.art ?? 'GEMAPPT'}
              disabled={artLaeuft}
              onChange={(e) => onArt(e.target.value as AdressArt)}
              className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] text-slate-600 disabled:opacity-50"
              title="Liefert dieselbe Seite, oder leitet mit 301 auf die führende Adresse?"
            >
              <option value="GEMAPPT">zeigt die Seite</option>
              <option value="WEITERLEITUNG">leitet weiter (301)</option>
            </select>
          )}
          {onEntfernen && (
            <button
              onClick={onEntfernen}
              title="Adresse entfernen"
              className="text-slate-300 opacity-0 transition group-hover:opacity-100 hover:text-red-600"
            >
              <Trash2 size={12} />
            </button>
          )}
          <ExternalLink size={11} className="text-slate-300" />
        </span>
      </div>
      {/* Der Befund steht nur da, wenn er etwas zu sagen hat. Ein „alles gut"
          unter jeder gruenen Zeile waere Rauschen. */}
      {befund && befund.ampel !== 'gruen' && (
        <p className="ml-4 mt-0.5 text-[11px] leading-snug text-slate-500">{befund.befund}</p>
      )}
    </div>
  );
}

/**
 * Was zu tun ist, wenn der PC den DNS-Eintrag nicht selbst setzen konnte.
 *
 * Die häufigste Ursache ist keine Störung: die Zone liegt nicht bei uns oder
 * unser Token sieht sie nicht. Dann muss jemand den A-Record anlegen — und
 * zwar mit genau der Adresse des Servers, auf dem die Instanz läuft.
 */
function DnsHinweis({
  hostname,
  ip,
  serverName,
  meldung,
}: {
  hostname: string;
  ip: string | null;
  serverName: string | null;
  meldung: string;
}) {
  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
      <p className="text-xs font-medium text-amber-900">DNS muss von Hand gesetzt werden</p>
      <p className="mt-0.5 text-[11px] text-amber-800">{meldung}</p>
      {ip && (
        <div className="mt-2 rounded border border-amber-200 bg-surface px-2 py-1.5">
          <table className="w-full text-[11px]">
            <tbody className="font-mono">
              <tr>
                <td className="pr-3 text-slate-400">Typ</td>
                <td className="text-slate-700">A</td>
              </tr>
              <tr>
                <td className="pr-3 text-slate-400">Name</td>
                <td className="text-slate-700">{hostname}</td>
              </tr>
              <tr>
                <td className="pr-3 text-slate-400">Wert</td>
                <td className="text-slate-700">{ip}</td>
              </tr>
            </tbody>
          </table>
          {serverName && (
            <p className="mt-1 text-[11px] text-slate-400">
              Das ist die Adresse von {serverName}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdressenKarte({ instanceId }: { instanceId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['instanz-adressen', instanceId],
    queryFn: () => instanzAdressen.list(instanceId),
  });

  const [offen, setOffen] = useState(false);
  const [hostname, setHostname] = useState('');
  const [forwardHost, setForwardHost] = useState('');
  const [forwardPort, setForwardPort] = useState('');
  const [dnsSelbst, setDnsSelbst] = useState(true);
  const [fehler, setFehler] = useState('');
  const [ergebnis, setErgebnis] = useState<{
    hostname: string;
    dns: { versucht: boolean; ok: boolean; meldung: string };
    server: { ipAddress: string; hostname: string } | null;
  } | null>(null);

  const anlegen = useMutation({
    mutationFn: () =>
      instanzAdressen.anlegen(instanceId, {
        hostname: hostname.trim().toLowerCase(),
        forwardHost: forwardHost.trim(),
        forwardPort: forwardPort ? Number(forwardPort) : undefined,
        dnsSelbst,
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['instanz-adressen', instanceId] });
      setErgebnis({ hostname: r.hostname, dns: r.dns, server: r.server });
      setHostname('');
      setFehler('');
      setOffen(false);
    },
    onError: (err: unknown) =>
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Die Adresse ließ sich nicht hinterlegen.',
      ),
  });

  const entfernen = useMutation({
    mutationFn: (adresseId: string) => instanzAdressen.entfernen(instanceId, adresseId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instanz-adressen', instanceId] }),
  });

  /**
   * Die Ampeln. Laeuft von selbst los, sobald jemand die Instanz ansieht.
   *
   * `staleTime` eine Minute: die Seite wird beim Hin- und Herwechseln zwischen
   * den Reitern sonst bei jedem Klick neu gegen alle Adressen geprueft, und das
   * sind echte Anfragen nach draussen.
   */
  const pruefung = useQuery({
    queryKey: ['instanz-adressen-pruefung', instanceId],
    queryFn: () => instanzAdressen.pruefen(instanceId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const befundFuer = (hostname: string): AdressBefund | undefined =>
    pruefung.data?.befunde.find((b) => b.hostname === hostname);

  const artSetzen = useMutation({
    mutationFn: ({ id, art }: { id: string; art: AdressArt }) =>
      instanzAdressen.artSetzen(instanceId, id, art),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instanz-adressen', instanceId] });
      // Die Pruefung NICHT gleich neu anstossen: die Weiterleitung entsteht
      // erst beim naechsten Ausrollen, eine sofortige Pruefung zeigte also
      // nur den alten Zustand und saehe wie ein Fehler aus.
    },
  });

  if (isLoading) return <div className="h-24 animate-pulse rounded-xl bg-slate-100" />;
  if (!data) return null;

  // Seit dem Umzug des Knopfes in den SQL-Reiter steht pgAdmin hier als
  // gewöhnliche Adresse in der Liste — dort gehört es als Adresse hin. Der
  // Weg dorthin, den man sucht, wenn gerade etwas klemmt, steht bei der
  // Datenbank.
  const uebrige = data.weitere;

  // Frueher stand hier ein `return null`, wenn es nichts anzuzeigen gab. Das
  // geht nicht mehr: die Karte ist der Ort, an dem man eine Adresse HINZUFUEGT,
  // und der darf nicht verschwinden, nur weil noch keine da ist.

  return (
    <div className="rounded-xl border border-slate-200 bg-surface p-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Globe size={16} className="text-brand-600" />
          <h3 className="text-sm font-semibold text-slate-800">Adressen</h3>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => pruefung.refetch()}
            disabled={pruefung.isFetching}
            title="Alle Adressen erneut anklopfen"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 disabled:opacity-50"
          >
            <RefreshCw size={11} className={pruefung.isFetching ? 'animate-spin' : ''} />
            {pruefung.isFetching ? 'prüft…' : 'prüfen'}
          </button>
          <button
            onClick={() => { setOffen((v) => !v); setErgebnis(null); setFehler(''); }}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
          >
            <Plus size={12} /> Adresse hinzufügen
          </button>
        </div>
      </div>

      {artSetzen.isSuccess && (
        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800">
          Gespeichert. Die Weiterleitung entsteht als Label am Container — wirksam wird sie
          beim nächsten Ausrollen dieser Instanz.
        </p>
      )}

      <div className="mt-3 divide-y divide-slate-100">
        {data.haupt && (
          <Zeile
            a={{ hostname: data.haupt, notiz: 'führend' }}
            haupt
            befund={befundFuer(data.haupt)}
            laedt={pruefung.isFetching}
          />
        )}
        {uebrige.map((a) => (
          <Zeile
            key={a.id}
            a={a}
            befund={befundFuer(a.hostname)}
            laedt={pruefung.isFetching}
            onArt={(art) => artSetzen.mutate({ id: a.id, art })}
            artLaeuft={artSetzen.isPending}
            onEntfernen={() => {
              if (confirm(`${a.hostname} aus dieser Instanz entfernen?`)) entfernen.mutate(a.id);
            }}
          />
        ))}
        {!data.haupt && !uebrige.length && (
          <p className="py-2 text-xs text-slate-400">
            Noch keine Adresse hinterlegt.
          </p>
        )}
      </div>

      {/* ══ EINE ADRESSE HINZUFUEGEN ═══════════════════════════════════════
          Der Endpunkt dafuer gab es laengst, das Formular nicht — wer eine
          zweite Adresse brauchte, musste die API bemuehen. */}
      {offen && (
        <form
          onSubmit={(e) => { e.preventDefault(); setFehler(''); anlegen.mutate(); }}
          className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3"
        >
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-slate-600">
              Adresse
              <input
                value={hostname}
                onChange={(e) => setHostname(e.target.value)}
                placeholder="shop.kunde.de"
                required
                className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
              />
            </label>
            <label className="text-[11px] text-slate-600">
              Ziel-Container
              <input
                value={forwardHost}
                onChange={(e) => setForwardHost(e.target.value)}
                placeholder="web-bryve-web_bryve"
                required
                className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
              />
            </label>
            <label className="text-[11px] text-slate-600">
              Port
              <input
                value={forwardPort}
                onChange={(e) => setForwardPort(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="80"
                className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
              />
            </label>
            <label className="flex items-end gap-2 pb-1 text-[11px] text-slate-600">
              <input
                type="checkbox"
                checked={dnsSelbst}
                onChange={(e) => setDnsSelbst(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span>
                DNS-Eintrag über den PC setzen
                <span className="block text-slate-400">
                  Geht nur, wenn die Zone bei Cloudflare liegt und wir sie sehen.
                </span>
              </span>
            </label>
          </div>
          {fehler && <p className="text-[11px] text-red-600">{fehler}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={anlegen.isPending}
              className="rounded bg-brand-600 px-3 py-1 text-xs text-surface hover:bg-brand-700 disabled:opacity-50"
            >
              {anlegen.isPending ? 'wird angelegt …' : 'Hinterlegen'}
            </button>
            <button
              type="button"
              onClick={() => { setOffen(false); setFehler(''); }}
              className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-600 hover:bg-slate-100"
            >
              Abbrechen
            </button>
          </div>
          <p className="text-[11px] text-slate-500">
            Der Proxy-Eintrag entsteht beim nächsten Ausrollen — dann wird auch das
            Zertifikat geholt.
          </p>
        </form>
      )}

      {/* Was aus dem DNS-Versuch geworden ist. Bei Erfolg eine Zeile, sonst die
          Anleitung — der Fall „Zone nicht bei uns" ist kein Fehler, sondern
          Arbeit fuer jemand anderen. */}
      {ergebnis && (
        ergebnis.dns.ok ? (
          <p className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            {ergebnis.hostname}: {ergebnis.dns.meldung}
          </p>
        ) : (
          <DnsHinweis
            hostname={ergebnis.hostname}
            ip={ergebnis.server?.ipAddress ?? null}
            serverName={ergebnis.server?.hostname ?? null}
            meldung={ergebnis.dns.meldung}
          />
        )
      )}

      {/* ══ KUNDENDOMAIN ═══════════════════════════════════════════════════
          Bei `MANUELL` zeigt der Kunde selbst — dann gehoert hierher, WAS er
          eintragen muss. Die Frage kommt spaeter wieder: wenn er nachfragt,
          und nach jedem Serverwechsel. */}
      {data.dnsPflege === 'MANUELL' && data.haupt && (
        <DnsAnleitung
          hostname={data.haupt}
          domain={data.domain ?? null}
          ipv4={data.server?.ipAddress ?? null}
          ipv6={data.server?.ipv6Address ?? null}
          serverName={data.server?.hostname ?? null}
          weitereNamen={data.weitere.map((a) => a.hostname)}
        />
      )}

    </div>
  );
}
